import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

// Types
interface MenuProps<K extends string | number | object> {
	onClickMenu: (key: K) => void;
	onStatus?: (isOpen: boolean) => void;
	children: React.ReactNode;
	className?: string;
}

export type MenuAlignment = "left" | "center" | "right";
export type MenuDirection = "above" | "below";

interface MenuBadgeProps {
	align?: MenuAlignment;
	direction?: MenuDirection;
	children: React.ReactNode;
	className?: string;
}

interface MenuContainerProps {
	className?: string;
	children: React.ReactNode;
}

interface MenuItemElementProps<K extends string | number | object> {
	itemKey: K;
	children: React.ReactNode;
	className?: string;
}

// Context for menu state
interface MenuContextType<K extends string | number | object> {
	isOpen: boolean;
	setIsOpen: (open: boolean) => void;
	onClickMenu: (key: K) => void;
	badgeRef: React.RefObject<HTMLDivElement>;
	direction: "above" | "below";
	align: "left" | "center" | "right";
}

const MenuContext = React.createContext<MenuContextType<any> | null>(null);

// Hook to use menu context
const useMenuContext = <K extends string | number | object>() => {
	const context = React.useContext(MenuContext) as MenuContextType<K> | null;
	if (!context) {
		console.log(`context: ${context}`);
		throw new Error("Menu components must be used within a Menu component");
	}
	return context;
};

// Menu Badge Component
const MenuBadge: React.FC<MenuBadgeProps> = ({
	align = "left",
	direction = "below",
	children,
	className = ""
}) => {
	const { isOpen, setIsOpen, badgeRef } = useMenuContext();

	return (
		<div
			ref={badgeRef}
			className={`cursor-pointer select-none ${className}`}
			onClick={() => setIsOpen(!isOpen)}
		>
			{children}
		</div>
	);
};

// Menu Container Component
const MenuContainer: React.FC<MenuContainerProps> = ({
	className = "rounded-lg border border-gray-200 shadow-lg",
	children
}) => {
	const { isOpen, direction, align, badgeRef } = useMenuContext();

	if (!isOpen || !badgeRef.current) return null;

	const rect = badgeRef.current.getBoundingClientRect();

	const style: React.CSSProperties = {};
	let transformClass = "";

	// Vertical positioning
	if (direction === "above") {
		style.bottom = window.innerHeight - rect.top + 4;
	} else {
		style.top = rect.bottom + 4;
	}

	// Horizontal alignment
	if (align === "right") {
		style.left = rect.right;
		transformClass = "-translate-x-full";
	} else if (align === "center") {
		style.left = rect.left + rect.width / 2;
		transformClass = "-translate-x-1/2";
	} else {
		style.left = rect.left;
	}

	return createPortal(
		<div
			style={style}
			className={`fixed z-menu min-w-max ${transformClass} ${className}`}
			onMouseDown={(e) => e.stopPropagation()}
		>
			{children}
		</div>,
		document.getElementById("root") ?? document.body
	);
};

// Menu Item Element Component
const MenuItemElement = <K extends string | number | object>({
	itemKey,
	children,
	className = "hover:bg-gray-100 px-3 py-2 first:rounded-t-lg last:rounded-b-lg"
}: MenuItemElementProps<K>) => {
	const { onClickMenu, setIsOpen } = useMenuContext<K>();

	const handleClick = () => {
		onClickMenu(itemKey);
		setIsOpen(false);
	};

	return (
		<div className={`cursor-pointer ${className}`} onClick={handleClick}>
			{children}
		</div>
	);
};

// Main Menu Component
const Menu = <K extends string | number | object>({
	onClickMenu,
	onStatus,
	children,
	className = ""
}: MenuProps<K>) => {
	const [isOpen, setIsOpen] = useState(false);
	const badgeRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	// Extract props from MenuBadge child
	const badgeChild: React.FC<MenuBadgeProps> | unknown = React.Children.toArray(
		children
	).find((child) => React.isValidElement(child) && child.type === MenuBadge);

	const direction = !React.isValidElement(badgeChild)
		? "below"
		: (badgeChild.props as MenuBadgeProps).direction || "below";
	const align = !React.isValidElement(badgeChild)
		? "left"
		: (badgeChild.props as MenuBadgeProps).align || "left";

	// Capture-phase listener catches scroll from any ancestor container, not just window.
	// Closes the menu because the portal position is computed once at open time and won't
	// follow the badge if a nested scroll container moves it.
	useEffect(() => {
		if (!isOpen) return;
		const close = () => setIsOpen(false);
		window.addEventListener("scroll", close, { capture: true, passive: true });
		return () => window.removeEventListener("scroll", close, { capture: true });
	}, [isOpen]);

	// Handle outside clicks
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (!menuRef.current?.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	// Notify parent of status change
	useEffect(() => {
		if (onStatus) {
			onStatus(isOpen);
		}
	}, [isOpen, onStatus]);

	const contextValue: MenuContextType<K> = {
		isOpen,
		setIsOpen,
		onClickMenu,
		badgeRef,
		direction,
		align
	};

	return (
		<MenuContext.Provider value={contextValue}>
			<div ref={menuRef} className={"relative inline-block " + className}>
				{children}
			</div>
		</MenuContext.Provider>
	);
};

// Export components
export { Menu, MenuBadge, MenuContainer, MenuItemElement };
